import React, { useState, useEffect } from 'react';
import { Star, MapPin, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { getCollegeListingSeoSettings } from '../lib/seoManager';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';
import toast from 'react-hot-toast';

const CollegeReviews = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const categories = ['all', 'Engineering', 'Medical', 'Law', 'Management'];
  const states = [
    'all',
    'Andhra Pradesh',
    'Arunachal Pradesh', 
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];

  useEffect(() => {
    fetchColleges();
  }, [selectedCategory, selectedState]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchColleges();
    }, 500); // 500ms delay for search

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery]);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const data = await collegeService.getAll({
        category: selectedCategory,
        state: selectedState,
        search: searchQuery
      });
      
      setColleges(data);
      
      if (data.length === 0 && !searchQuery && selectedCategory === 'all' && selectedState === 'all') {
        toast.info('No colleges found in database. Please add some colleges first.');
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Failed to load colleges');
      setColleges([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading colleges...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={selectedCategory !== 'all' 
          ? `Best ${selectedCategory} Colleges in India - Reviews & Ratings | Grad Vikalp`
          : selectedState !== 'all' 
            ? `Top Colleges in ${selectedState} - Ratings & Reviews | Grad Vikalp`
            : 'College Reviews - Verified Student Reviews | Grad Vikalp'}
        description={selectedCategory !== 'all' 
          ? `Find the best ${selectedCategory} colleges in India. Compare fees, placements, facilities, and read verified student reviews. Make informed decisions about your ${selectedCategory.toLowerCase()} education.`
          : selectedState !== 'all' 
            ? `Discover the best colleges in ${selectedState}. Compare ratings, fees, facilities, and read authentic student reviews. Find engineering, medical, and other top colleges in ${selectedState}.`
            : 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India. Make informed decisions with verified student experiences and ratings.'}
        keywords={selectedCategory !== 'all' 
          ? `${selectedCategory.toLowerCase()} colleges, top ${selectedCategory.toLowerCase()} colleges, best ${selectedCategory.toLowerCase()} institutes, ${selectedCategory.toLowerCase()} education, ${selectedCategory.toLowerCase()} courses, college reviews, college ratings`
          : selectedState !== 'all' 
            ? `colleges in ${selectedState.toLowerCase()}, ${selectedState.toLowerCase()} colleges, top colleges in ${selectedState.toLowerCase()}, engineering colleges in ${selectedState.toLowerCase()}, medical colleges in ${selectedState.toLowerCase()}, college reviews, college ratings`
            : 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews, authentic college feedback, college experiences, verified reviews'}
        url="/college-reviews"
        type="website"
        pageKey="college-reviews"
      />
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-dark mb-3">College Reviews</h1>
          <p className="lead text-muted">Discover colleges through authentic student reviews</p>
        </div>

        {/* Search and Filters */}
        <div className="card border-0 shadow-lg mb-5">
          <div className="card-body p-4">
            <div className="row g-3">
              {/* Search Bar */}
              <div className="col-lg-6">
                <div className="position-relative">
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={20} />
                  <input
                    type="text"
                    placeholder="Search colleges by name or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control form-control-lg ps-5"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="col-lg-3">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>

              {/* State Filter */}
              <div className="col-lg-3">
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="form-select form-select-lg"
                >
                  {states.map(state => (
                    <option key={state} value={state}>
                      {state === 'all' ? 'All States' : state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-muted">
            Showing {colleges.length} college{colleges.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* College Cards */}
        <div className="row g-4">
          {colleges.map((college) => (
            <div key={college.id} className="col-lg-4 col-md-6">
              <Link
                to={`/college/${college.slug}`}
                className="card h-100 border-0 shadow-lg text-decoration-none college-card"
              >
                <div className="position-relative">
                  <OptimizedImage
                    src={college.image_url}
                    alt={`${college.name} campus - Premier ${college.category} college in ${college.location}, ${college.state} with ${college.rating}/5 rating`}
                    title={`${college.name} | ${college.type} ${college.category} College | Student Rating: ${college.rating}/5 | Fees: ${college.fees} | Established: ${college.established} | Location: ${college.location}, ${college.state}`}
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 300px"
                  />
                  <div className="position-absolute top-0 end-0 m-3">
                    <span className="badge bg-primary fs-6 px-3 py-2">{college.category}</span>
                  </div>
                </div>

                <div className="card-body p-4">
                  <h2 className="h5 fw-bold text-dark mb-2">{college.name}</h2>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-1">
                      <Star className="star-rating" size={16} fill="currentColor" />
                      <span className="fw-semibold text-dark">{college.rating}</span>
                    </div>
                    
                    <div className="d-flex align-items-center gap-1 text-muted">
                      <MapPin size={14} />
                      <span className="small">{college.location}</span>
                    </div>
                  </div>

                  <p className="text-muted small mb-4 line-clamp-2">
                    {college.description}
                  </p>
                  
                  {college.courses && college.courses.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {college.courses.slice(0, 3).map((course, idx) => (
                        <span key={idx} className="badge bg-light text-primary small">
                          {course}
                        </span>
                      ))}
                      {college.courses.length > 3 && (
                        <span className="badge bg-light text-primary small">
                          +{college.courses.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  <div className="vstack gap-2">
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Fees:</span>
                      <span className="fw-semibold text-dark">{college.fees}</span>
                    </div>
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Cutoff:</span>
                      <span className="fw-semibold text-dark">{college.cutoff}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {colleges.length === 0 && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                    <Search className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="h4 text-dark mb-3">No colleges found</h3>
                <p className="text-muted mb-4">
                  {searchQuery || selectedCategory !== 'all' || selectedState !== 'all' 
                    ? 'No colleges match your current search criteria. Try adjusting your filters or search terms.'
                    : 'No colleges have been added to the database yet. Please check back later or contact support.'
                  }
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <button 
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedState('all');
                    }}
                    className="btn btn-primary"
                  >
                    Clear Filters
                  </button>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeReviews;